// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Center, Loader, Select, Space, Text } from '@mantine/core';
import { FC, useContext, useState } from 'react';
import { i18n } from '@lingui/core';
import { IconChevronDown, IconSend, IconTemplate } from '@tabler/icons';
import { useParams } from 'react-router-dom';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { EventBaseInput, EventRichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import { AgencyTemplateDTO } from '../../../../Types/MessageT';
import { DataS } from '../../../../Service/DataS';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import Attachments from '../../../../Organismns/Attachments';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import { Contact } from '../../../../Service/ContactS';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { useRecommendationMail } from '../../../../Service/hooks/useRecommendationMail';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { RecommendationDto } from '../../../../Types/RecommendationT';
import { EventContext } from '../../../../Service/Context/EventContext';
import { StringUtils } from '../../../../Utils/utils';
import { EventS } from '../../../../Service/EventS';
import { openAttachmentSelectionModal } from '../../../../Atoms/AttachmentSelectionModal';
import { TiptapS } from '../../../../Service/TiptapS';

interface Props {
  recommendation: RecommendationDto;
  recommendedContact: Contact;
  closeModal: () => void;
}

const SendMailModal: FC<Props> = ({ recommendation, closeModal, recommendedContact }) => {
  const { id, lastContactShare } = recommendation;
  const { dates } = useContext<any>(EventContext);
  const { id: eventId } = useParams();
  const { profileId } = useJwtClaims();
  const [loading, setLoading] = useState(false);
  const { templates } = useAgencyTemplates();
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const recommendationDates = RecommendationS.extractRecommendationDates(dates, recommendation);

  const { subject, message, attachments, handlers, updateProp, lastUpdate } = useRecommendationMail(
    'REGULAR',
    recommendation,
    selectedTemplate,
  );
  const { setState: setAttachments, remove: removeAttachment } = handlers;

  const sendInfoEmail = async () => {
    try {
      setLoading(true);
      const placeholdersResolved = await PlaceholderS.checkPlaceholders({
        profileId,
        eventId: Number(eventId),
        message,
        subject,
      });
      if (placeholdersResolved) {
        const sent = await RecommendationS.sendInfoMail(id, {
          subject,
          message,
          attachments,
          newVersion: true,
          includeCustomerDetails: lastContactShare !== null,
        });
        if (sent) {
          closeModal();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Text mb="xs"> Schreibe eine E-Mail mit weiteren Details zur Vermittlung an den Dienstleister.</Text>
      <Select
        mb="xs"
        label={
          <Text size="xs" color="dimmed">
            Vorlage
          </Text>
        }
        nothingFound="Keine Vorlagen"
        icon={<IconTemplate size={20} />}
        data={DataS.getAgencyTemplateOptions(templates)}
        value={selectedTemplate ? selectedTemplate.id : undefined}
        onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
        styles={{ rightSection: { pointerEvents: 'none' } }}
        rightSection={<IconChevronDown size={14} />}
        rightSectionWidth={25}
      />
      <EventBaseInput
        updateTrigger={lastUpdate}
        toolbar={<BaseToolbar />}
        label="Betreff"
        value={subject}
        onUpdate={(html) => updateProp({ subject: html })}
      />
      <Space h="sm" />
      <Attachments
        attachments={attachments}
        documentBlueprints={[]}
        deleteFileAttachment={removeAttachment}
        deleteDocumentAttachment={undefined}
        eventId={undefined}
      />
      <Space h={attachments.length > 0 ? 'xl' : 'sm'} />
      <Text mb="sm">{`Hey ${RecommendationS.getGreeting(recommendedContact)},`} </Text>
      <Text mb="sm">
        {`bzgl. dem Event am ${StringUtils.concatStrings(recommendationDates.map(EventS.formatEventDate))}`}
      </Text>
      <EventRichInput
        isEmailInput
        updateTrigger={lastUpdate}
        styles={TiptapS.TextAreaStyles}
        label="Nachricht an den Dienstleister"
        value={message}
        onUpdate={(html) => updateProp({ message: html })}
        toolbar={
          <TemplateMessageToolbar
            addAttachment={() =>
              eventId &&
              profileId &&
              openAttachmentSelectionModal({
                eventId,
                profileId,
                initialAttachmnets: attachments,
                onSave: (_attachments) => {
                  setAttachments(_attachments);
                  return Promise.resolve(true);
                },
              })
            }
            saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments } })}
          />
        }
      />
      <Text mt={5} size="xs" color="dimmed">
        Hinweis: Diese Nachricht wird zur Zeit noch nicht im System gespeichert - Du kannst Sie also im Nachhinein nicht
        noch einmal einsehen.
      </Text>
      <Center>
        <Button
          mt="xl"
          mb="xs"
          onClick={sendInfoEmail}
          disabled={loading}
          leftIcon={loading ? <Loader size="xs" /> : <IconSend size={16} />}
          sx={{ width: 250 }}
        >
          {i18n._('actions.send')}
        </Button>
      </Center>
    </>
  );
};

export default SendMailModal;
